/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { Link } from "react-router-dom";
import { setShowNotification } from "../../actions/notification";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import IconComponent from "@CubeComponents/icons";
import axiosWrapper from "../../services/axiosWrapper";
import type { GetActionableCountResponse, ActionableCountRecord } from "@ServerTypes/projects/project.schema";
import { formCategories } from "@Helpers";
import { RootState } from "@Reducers/index";
import type { CustomModuleItem } from "@ServerTypes/portal/portal.schema";
import { untranslated, useTranslation } from "@i18n";

interface ActionCardsProps {
  onCountChange?: (count: number) => void;
}

/**
 * One "Take Action" tile. `allow` is `boolean | object` because the portal layout declares a
 * module's `ENABLE` as either a boolean or a nested capability record, and this component only
 * tests it for truthiness. The custom-module tiles pushed below supply optional label/icon/module.
 */
type ActionTile = {
  id: number;
  count: number;
  label: string | undefined;
  icon: string | undefined;
  module: string | undefined;
  allow: boolean | object | undefined;
  overDueCount: number | undefined;
  next3DaysCount: number | undefined;
  toolContent: string;
};

function ActionCards({ onCountChange }: ActionCardsProps) {
  const [moduleCount, setModuleCount] = useState<ActionableCountRecord>({});
  const [actions, setActions] = useState<ActionTile[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const customModules = useSelector((state: RootState) => state.customModules);
  const layout = useSelector((state: RootState) => state.layout);

  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const getModulesCount = async () => {
    setLoading(true);
    const res = await axiosWrapper.post<GetActionableCountResponse>(`${getBackendUrl()}/projects/getActionableCount`);
    if (res.data.status) {
      setModuleCount(res.data.data ?? {});
    }
    setLoading(false);
  };

  useEffect(() => {
    getModulesCount();
  }, []);

  useEffect(() => {
    const modules: ActionTile[] = [
      {
        id: 1,
        count: moduleCount.reviews?.count || 0,
        label: "Reviews",
        icon: "Review",
        module: "reviews",
        allow: layout.REVIEWFLOW?.ENABLE,
        overDueCount: moduleCount.reviews?.overdueCount,
        next3DaysCount: moduleCount.reviews?.next3DaysCount,
        toolContent: `${moduleCount.reviews?.overdueCount || moduleCount.reviews?.next3DaysCount} Review(s) ${
          moduleCount.reviews?.overdueCount ? "exceeded due date" : "approaching due date in 3 day(s)"
        }`,
      },
      {
        id: 4,
        count: moduleCount.issues?.count || 0,
        label: "Issues",
        icon: "IssuesIcon",
        module: "issues",
        allow: layout.ISSUES?.ENABLE,
        overDueCount: moduleCount.issues?.overdueCount,
        // Issues surface the "approaching due date" badge for the next 7 days (other modules use 3 days).
        // The shared badge condition reads `next3DaysCount`, so we feed it the 7-day count here.
        next3DaysCount: moduleCount.issues?.next7DaysCount,
        toolContent: `${moduleCount.issues?.overdueCount || moduleCount.issues?.next7DaysCount} Issue(s) ${
          moduleCount.issues?.overdueCount ? "exceeded due date" : "approaching due date in 7 day(s)"
        }`,
      },
      {
        id: 2,
        count: moduleCount[formCategories.inspection]?.count || 0,
        label: "Inspections",
        icon: "inspectionForm",
        module: formCategories.inspection,
        allow: layout.INSPECTION?.ENABLE,
        overDueCount: moduleCount[formCategories.inspection]?.overdueCount,
        next3DaysCount: moduleCount[formCategories.inspection]?.next3DaysCount,
        toolContent: `${
          moduleCount[formCategories.inspection]?.overdueCount || moduleCount[formCategories.inspection]?.next3DaysCount
        } Inspections ${
          moduleCount[formCategories.inspection]?.overdueCount
            ? "exceeded due date"
            : "approaching due date in 3 day(s)"
        }`,
      },
      {
        id: 3,
        count: moduleCount[formCategories.progressUpdates]?.count || 0,
        label: "Progress updates",
        icon: "ProgressUpdates",
        module: formCategories.progressUpdates,
        allow: layout.PROGRESS_UPDATES?.ENABLE,
        overDueCount: moduleCount[formCategories.progressUpdates]?.overdueCount,
        next3DaysCount: moduleCount[formCategories.progressUpdates]?.next3DaysCount,
        toolContent: `${
          moduleCount[formCategories.progressUpdates]?.overdueCount ||
          moduleCount[formCategories.progressUpdates]?.next3DaysCount
        } Progress updates ${
          moduleCount[formCategories.progressUpdates]?.overdueCount
            ? "exceeded due date"
            : "approaching due date in 3 day(s)"
        }`,
      },
    ];
    customModules.map((module: CustomModuleItem, index: number) => {
      modules.push({
        id: index + 6,
        count: moduleCount[module.resourceType!]?.count || 0,
        label: module.name,
        icon: module.icon,
        module: module.resourceType,
        allow: true,
        overDueCount: moduleCount[module.resourceType!]?.overdueCount,
        next3DaysCount: moduleCount[module.resourceType!]?.next3DaysCount,
        toolContent: `${
          moduleCount[module.resourceType!]?.overdueCount || moduleCount[module.resourceType!]?.next3DaysCount
        } ${module.name} ${
          moduleCount[module.resourceType!]?.overdueCount ? "exceeded due date" : "approaching due date in 3 day(s)"
        }`,
      });
    });
    setActions(modules);
  }, [customModules, moduleCount]);

  useEffect(() => {
    let total = 0;
    actions.map((module: ActionTile) => {
      if (module.allow) {
        total += module.count;
      }
    });
    setTotalCount(total);
    if (!loading) {
      onCountChange?.(total);
    }
  }, [actions, onCountChange, loading]);

  const dispatch = useDispatch();
  const closeNotificationModal = () => {
    dispatch(setShowNotification(false));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <IconComponent name="loader-icon" className="animate-spin" width="2rem" height="2rem" />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center">
      <div className="w-full max-w-3xl p-3">
        <div className="ps-8 py-4 font-semibold">
          {t("Take Action")}{" "}
          <span className="inline-flex items-center p-1 ms-2 text-sm font-bold bg-[#ECF3FE] rounded-xs">
            {totalCount}
          </span>
        </div>
        <hr className="my-2 border-blue-50" />
      </div>
      <div className="flex flex-wrap gap-3 pe-6 ps-8 w-full">
        {actions.map(
          (action: ActionTile) =>
            action.allow &&
            action.count > 0 && (
              <Link
                to={`/notifications?section=takeAction&module=${action.module}`}
                onClick={closeNotificationModal}
                className="no-underline text-inherit"
                key={action.id}
              >
                <div className="flex items-center justify-between rounded-lg p-4 w-full border cursor-pointer">
                  <div className="flex flex-wrap gap-3 items-center min-w-0">
                    <span>
                      <IconComponent name={action.icon} width="1.5rem" height="1.5rem" />
                    </span>
                    <span className="text-xl font-bold">{action.count}</span>
                    <span className="text-base sm:text-lg font-semibold whitespace-nowrap">{action.label}</span>
                    {/* Both counts are optional on the actionable-count record; the comparison
                        against 0 is the existing undefined-tolerant behaviour. Erased assertions. */}
                    {(action.overDueCount! > 0 || action.next3DaysCount! > 0) && (
                      <ToolTipComponent
                        position="bottom"
                        // i18n GAP: these summaries are assembled from untranslated literals
                        // above, so they go through `untranslated()` rather than `t()`. Fixing this
                        // needs interpolated keys added to public/locales/en.json.
                        content={untranslated(action.toolContent)}
                        className="max-w-50 wrap-break-word"
                      >
                        <span
                          className={`w-5 h-5 flex items-center justify-center rounded-full text-white text-xs cursor-default ${
                            action.overDueCount ? "bg-red-500" : "bg-yellow-400"
                          }`}
                        >
                          !
                        </span>
                      </ToolTipComponent>
                    )}
                  </div>
                </div>
              </Link>
            ),
        )}
      </div>
    </div>
  );
}

export default ActionCards;
